import React, { useEffect, useState, useMemo } from "react";
import { InputField } from "@cakrawalajakarta/shared-components";
import { AnimatePresence, motion } from "framer-motion";
import { useNotification } from "@cakrawalajakarta/shared-components/common/NotificationContext";
import { db } from "@lib/firebase";
import { doc, setDoc } from "firebase/firestore";
import { serverTimestamp } from "firebase/firestore";
import { getAuth } from "firebase/auth";

export const InputFieldRace = ({
  formData,
  handleChange,
  isFormDisabled = false,
  labelColor,
  eventId,
  categoryName,
  updateUserData,
}) => {
  const { showNotification } = useNotification();
  const [countries, setCountries] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [rawCountries, setRawCountries] = useState([]);
  const [provinceIds, setProvinceIds] = useState({});
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState({
    countries: false,
    provinces: false,
    cities: false,
  });
  const [participantProvinces, setParticipantProvinces] = useState([
    [],
    [],
    [],
  ]);
  const [participantCities, setParticipantCities] = useState([[], [], []]);
  const [participantProvinceIds, setParticipantProvinceIds] = useState([
    {},
    {},
    {},
  ]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentParticipantIndex, setCurrentParticipantIndex] = useState(0);
  const [genderError, setGenderError] = useState("");

  const GEO_API_BASE = `${import.meta.env.VITE_API_BASE_URL}/api/geo`;
  const sizeChartUrl = "/jersey/sizechart.jpg";

  const cache = useMemo(() => new Map(), []);
  const isGroupRegistration =
    eventId === "JOTR" && categoryName === "Mix Group (20K)";

  const humanize = (str) => {
    if (!str) return "";
    const withSpaces = str.replace(/([A-Z])/g, " $1").trim();
    return withSpaces
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  // Fetch data from Firestore on component mount (only once)
  useEffect(() => {
    if (!isGroupRegistration) return;

    const fetchRegistrationData = async () => {
      const auth = getAuth();
      const userId = auth.currentUser?.uid;
      if (!userId || !eventId) {
        console.error(
          "Missing userId or eventId for fetching registration data"
        );
        showNotification(
          "error",
          "User or event information is incomplete."
        );
        return;
      }

      try {
        const docRef = doc(db, "registrations", `${userId}_${eventId}`);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          console.log("Fetched registration data from Firestore:", data);

          // Initialize default participant structure
          const defaultParticipant = {
            namaDepan: "",
            namaBelakang: "",
            bibName: "",
            telepon: "",
            nomorIdentitas: "",
            tanggalLahir: "",
            golonganDarah: "",
            sizeJersey: "",
            gender: "",
            negara: "",
            provinsi: "",
            kota: "",
            alamat: "",
            kontakDarurat: "",
            namaKontakDarurat: "",
            hubunganKontakDarurat: "",
            performanceIndex: "",
            stravaProfile: "",
          };

          // Ensure participants array has 3 elements, merging Firestore data
          const participants = Array(3)
            .fill()
            .map((_, index) => ({
              ...defaultParticipant,
              ...(data.participants && data.participants[index]
                ? data.participants[index]
                : {}),
            }));

          // Update formData with participants and teamName
          handleChange({
            target: {
              name: "participants",
              value: participants,
            },
          });

          if (data.teamName) {
            handleChange({
              target: {
                name: "teamName",
                value: data.teamName,
              },
            });
          }

          console.log("Applied registration data to formData:", {
            participants,
            teamName: data.teamName,
          });
        } else {
          console.log(
            "No registration data found in Firestore, using default formData."
          );
        }
      } catch (err) {
        console.error("Error fetching registration data:", err);
        showNotification("error", "Failed to load registration data from the server.");
      }
    };

    fetchRegistrationData();
  }, []); // Empty dependency array to run only once on mount

  const fetchWithCache = async (
    url,
    cacheKey,
    type,
    participantIndex = null
  ) => {
    if (cache.has(cacheKey)) {
      return cache.get(cacheKey);
    }
    setLoading((prev) => ({ ...prev, [type]: true }));
    try {
      const res = await fetch(url);
      const data = await res.json();
      if (data?.geonames) {
        cache.set(cacheKey, data);
        return data;
      } else {
        console.error(`No geonames data in response for ${cacheKey}:`, data);
        setErrors((prev) => ({
          ...prev,
          [participantIndex !== null ? `${type}_${participantIndex}` : type]:
            "Gagal memuat data lokasi. Silakan coba lagi nanti.",
        }));
        showNotification(
          "error",
          "Gagal memuat data lokasi. Silakan coba lagi nanti."
        );
        return null;
      }
    } catch (err) {
      console.error(`Error fetching ${cacheKey}:`, err);
      setErrors((prev) => ({
        ...prev,
        [participantIndex !== null ? `${type}_${participantIndex}` : type]:
          "Gagal memuat data lokasi. Silakan coba lagi nanti.",
      }));
      showNotification(
        "error",
        "Gagal memuat data lokasi. Silakan coba lagi nanti."
      );
      return null;
    } finally {
      setLoading((prev) => ({ ...prev, [type]: false }));
    }
  };

  useEffect(() => {
    fetchWithCache(
      `${GEO_API_BASE}/countries`,
      "countries",
      "countries"
    ).then((data) => {
      if (data?.geonames) {
        const countryList = data.geonames.map((country) => ({
          value: country.countryName,
          label: country.countryName,
        }));
        setCountries(countryList);
        setRawCountries(data.geonames);
      } else {
        console.warn("Using empty country list as fallback");
        setCountries([]);
        setRawCountries([]);
      }
    });
  }, [cache]);

  useEffect(() => {
    if (formData.negara && rawCountries.length > 0 && !isGroupRegistration) {
      setProvinces([]);
      setCities([]);
      const selectedCountry = rawCountries.find(
        (c) => c.countryName === formData.negara
      );
      if (!selectedCountry) {
        console.warn(`Country not found: ${formData.negara}`);
        return;
      }

      fetchWithCache(
        `${GEO_API_BASE}/children/${selectedCountry.geonameId}?lang=en`,
        `provinces_${selectedCountry.geonameId}`,
        "provinces"
      ).then((data) => {
        if (data?.geonames) {
          const provinceList = data.geonames.map((prov) => ({
            value: prov.name,
            label: prov.name,
          }));
          const idMap = {};
          data.geonames.forEach((prov) => {
            idMap[prov.name] = prov.geonameId;
          });
          setProvinces(provinceList);
          setProvinceIds(idMap);
        }
      });
    }
  }, [formData.negara, rawCountries, isGroupRegistration, cache]);

  useEffect(() => {
    if (
      formData.provinsi &&
      provinceIds[formData.provinsi] &&
      !isGroupRegistration
    ) {
      setCities([]);
      const provinceId = provinceIds[formData.provinsi];

      fetchWithCache(
        `${GEO_API_BASE}/children/${provinceId}?lang=en`,
        `cities_${provinceId}`,
        "cities"
      ).then((data) => {
        if (data?.geonames) {
          const cityList = data.geonames.map((city) => ({
            value: city.name,
            label: city.name,
          }));
          setCities(cityList);
        }
      });
    }
  }, [formData.provinsi, provinceIds, isGroupRegistration, cache]);

  useEffect(() => {
    if (isGroupRegistration && rawCountries.length > 0) {
      const participant = formData.participants[currentParticipantIndex];
      if (participant?.negara) {
        const selectedCountry = rawCountries.find(
          (c) => c.countryName === participant.negara
        );
        if (!selectedCountry) {
          console.warn(
            `Country not found for participant ${
              currentParticipantIndex + 1
            }: ${participant.negara}`
          );
          return;
        }

        fetchWithCache(
          `${GEO_API_BASE}/children/${selectedCountry.geonameId}?lang=en`,
          `provinces_${selectedCountry.geonameId}_${currentParticipantIndex}`,
          "provinces",
          currentParticipantIndex
        ).then((data) => {
          if (data?.geonames) {
            const provinceList = data.geonames.map((prov) => ({
              value: prov.name,
              label: prov.name,
            }));
            const idMap = {};
            data.geonames.forEach((prov) => {
              idMap[prov.name] = prov.geonameId;
            });
            setParticipantProvinces((prev) => {
              const newProvinces = [...prev];
              newProvinces[currentParticipantIndex] = provinceList;
              return newProvinces;
            });
            setParticipantProvinceIds((prev) => {
              const newIds = [...prev];
              newIds[currentParticipantIndex] = idMap;
              return newIds;
            });

            if (participant.provinsi && idMap[participant.provinsi]) {
              fetchWithCache(
                `${GEO_API_BASE}/children/${idMap[participant.provinsi]}?lang=en`,
                `cities_${
                  idMap[participant.provinsi]
                }_${currentParticipantIndex}`,
                "cities",
                currentParticipantIndex
              ).then((data) => {
                if (data?.geonames) {
                  const cityList = data.geonames.map((city) => ({
                    value: city.name,
                    label: city.name,
                  }));
                  setParticipantCities((prev) => {
                    const newCities = [...prev];
                    newCities[currentParticipantIndex] = cityList;
                    return newCities;
                  });
                }
              });
            }
          }
        });
      }
    }
  }, [
    formData.participants,
    rawCountries,
    isGroupRegistration,
    currentParticipantIndex,
    cache,
  ]);

  const validateField = (name, value, participantIndex = null) => {
    const label = humanize(name);

    if (value === undefined || value === null || value === "") {
      return `${label} is required`;
    }

    if (name === "teamName" && value) {
      if (!/^[A-Za-z0-9\s]{3,50}$/.test(value)) {
        return `${label} must be 3-50 characters, alphanumeric with spaces, for example: "Fast Running Team"`;
      }
    }
    if (name === "namaDepan" && value) {
      if (!/^[A-Za-z\s]{1,50}$/.test(value)) {
        return `${label} only letters and spaces allowed, maximum 50 characters, for example: "John"`;
      }
    }
    if (name === "namaBelakang" && value) {
      if (!/^[A-Za-z\s]{1,50}$/.test(value)) {
        return `${label} only letters and spaces allowed, maximum 50 characters, for example: "Doe"`;
      }
    }
    if (name === "bibName" && value) {
      if (!/^[A-Za-z0-9\s]{1,20}$/.test(value)) {
        return `${label} must be 1-20 alphanumeric characters and spaces, for example: "JOHN123"`;
      }
    }
    if (name === "telepon" && value) {
      if (!/^\+?\d{8,15}$/.test(value)) {
        return `${label} must be 8-15 digits, can start with "+", for example: "+6281234567890"`;
      }
    }
    if (name === "nomorIdentitas" && value) {
      if (!/^\d{16}$/.test(value)) {
        return `${label} (ID Card) must be 16 digits, for example: "1234567890123456"`;
      }
    }
    if (name === "tanggalLahir" && value) {
      const date = new Date(value);
      const eventDate = new Date("2025-09-27");
      const minAgeDate = new Date(
        eventDate.getFullYear() - 15,
        eventDate.getMonth(),
        eventDate.getDate()
      );
      if (isNaN(date.getTime()) || date >= eventDate) {
        return `${label} must be in the past before 27/9/2025, for example: "2010-09-27"`;
      }
      if (date > minAgeDate) {
        return `Participant must be at least 15 years old on 27/9/2025, for example: "2010-09-27" or earlier`;
      }
    }
    if (name === "golonganDarah" && value) {
      if (!["A", "B", "AB", "O"].includes(value)) {
        return `${label} must be A, B, AB, or O`;
      }
    }
    if (name === "sizeJersey" && value) {
      if (!["S", "M", "L", "XL", "XXL"].includes(value)) {
        return `${label} must be S, M, L, XL, or XXL`;
      }
    }
    if (name === "gender" && value) {
      if (!["Male", "Female"].includes(value)) {
        return `${label} must be Male or Female`;
      }
    }
    if (name === "negara" && value) {
      if (countries.length > 0 && !countries.some((c) => c.value === value)) {
        return `${label} must be selected from the available list, for example: "Indonesia"`;
      }
    }
    if (name === "provinsi" && value) {
      const provinceList =
        participantIndex !== null
          ? participantProvinces[participantIndex]
          : provinces;
      if (
        provinceList.length > 0 &&
        !provinceList.some((p) => p.value === value)
      ) {
        return `${label} must be selected from the available list, for example: "West Java"`;
      }
    }
    if (name === "kota" && value) {
      const cityList =
        participantIndex !== null
          ? participantCities[participantIndex]
          : cities;
      if (cityList.length > 0 && !cityList.some((c) => c.value === value)) {
        return `${label} must be selected from the available list, for example: "Jakarta"`;
      }
    }
    if (name === "alamat" && value) {
      if (!/^[A-Za-z0-9\s,.-/#()]{5,100}$/.test(value)) {
        return `${label} must be 5-100 characters, can be letters, numbers, spaces, commas, periods, hyphens, slashes, hashes, or parentheses, for example: "Merdeka Street No. 123"`;
      }
    }
    if (name === "kontakDarurat" && value) {
      if (!/^\+?\d{8,15}$/.test(value)) {
        return `${label} harus 8-15 digit, boleh diawali dengan "+", misalnya: "+6281234567890"`;
      }
    }
    if (name === "namaKontakDarurat" && value) {
      if (!/^[A-Za-z\s]{1,50}$/.test(value)) {
        return `${label} only letters and spaces allowed, maximum 50 characters, for example: "Jane"`;
      }
    }
    if (name === "hubunganKontakDarurat" && value) {
      if (!/^[A-Za-z\s]{1,50}$/.test(value)) {
        return `${label} only letters and spaces allowed, maximum 50 characters, for example: "Spouse"`;
      }
    }
    if (name === "performanceIndex" && value) {
      if (!/^\d{3}$/.test(value)) {
        return `${label} must be a 3-digit number, for example: "460"`;
      }
    }
    if (name === "stravaProfile" && value) {
      return "";
    }
    return "";
  };

  const validateIndividual = () => {
    const requiredFields = [
      "namaDepan",
      "namaBelakang",
      "telepon",
      "nomorIdentitas",
      "tanggalLahir",
      "gender",
      "negara",
      "provinsi",
      "kota",
      "alamat",
      "kontakDarurat",
      "namaKontakDarurat",
      "hubunganKontakDarurat",
      ...(eventId === "JOTR"
        ? [
            "bibName",
            "golonganDarah",
            "sizeJersey",
            "performanceIndex",
            "stravaProfile",
          ]
        : []),
    ];
    const errors = {};
    let isValid = true;

    requiredFields.forEach((field) => {
      const error = validateField(field, formData[field]);
      if (error && touched[field]) {
        errors[field] = error;
        isValid = false;
      }
    });

    return { isValid, errors };
  };

  const validateParticipant = (participantData, index) => {
    const requiredFields = [
      "namaDepan",
      "namaBelakang",
      "bibName",
      "telepon",
      "nomorIdentitas",
      "tanggalLahir",
      "golonganDarah",
      "sizeJersey",
      "gender",
      "negara",
      "provinsi",
      "kota",
      "alamat",
      "kontakDarurat",
      "namaKontakDarurat",
      "hubunganKontakDarurat",
      "performanceIndex",
      "stravaProfile",
    ];
    const errors = {};
    let isValid = true;

    requiredFields.forEach((field) => {
      const error = validateField(field, participantData[field], index);
      if (error) {
        errors[`${field}_${index}`] = error;
        isValid = false;
      }
    });

    return { isValid, errors };
  };

  const validateAllParticipants = () => {
    let allValid = true;
    let allErrors = {};
    const bibNames = new Set();

    setTouched((prev) => {
      const newTouched = { ...prev };
      formData.participants.forEach((participant, index) => {
        Object.keys(participant).forEach((field) => {
          newTouched[`${field}_${index}`] = true;
        });
      });
      return newTouched;
    });

    formData.participants.forEach((participant, index) => {
      const { isValid, errors } = validateParticipant(participant, index);
      console.log(`Validation for participant ${index + 1}:`, {
        isValid,
        errors,
      });
      if (!isValid) {
        allValid = false;
        allErrors = { ...allErrors, ...errors };
      }
      if (participant.bibName) {
        if (bibNames.has(participant.bibName)) {
          allErrors[`bibName_${index}`] = `BIB Name already used`;
          allValid = false;
        } else {
          bibNames.add(participant.bibName);
        }
      }
    });

    const allMale = formData.participants.every(
      (p) => p.gender === "Male"
    );
    if (allMale) {
      setGenderError(
        "Team cannot be all male. At least one participant must be female."
      );
      allValid = false;
    } else {
      setGenderError("");
    }

    setErrors(allErrors);
    return allValid;
  };

  const handleFieldChange = (e, participantIndex = null) => {
    const { name, value } = e.target;
    handleChange(e, participantIndex);
    setTouched((prev) => ({
      ...prev,
      [participantIndex !== null ? `${name}_${participantIndex}` : name]: true,
    }));
    const error = validateField(name, value, participantIndex);
    setErrors((prev) => ({
      ...prev,
      [participantIndex !== null ? `${name}_${participantIndex}` : name]: error,
    }));
  };

  const saveToFirestore = async (showSuccessNotification = false) => {
    const auth = getAuth();
    const userId = auth.currentUser?.uid;
    if (!userId || !eventId) {
      console.error("Missing userId or eventId for Firestore save");
      showNotification(
        "error",
        "Failed to save data: User or event information is incomplete."
      );
      return false;
    }

    try {
      const docRef = doc(db, "registrations", `${userId}_${eventId}`);
      const draftData = {
        participants: formData.participants,
        teamName: formData.teamName || "",
        eventId,
        userId,
        status: "draft",
        updatedAt: serverTimestamp(),
      };
      await setDoc(docRef, draftData);
      console.log(
        "Draft saved to Firestore for ID:",
        `${userId}_${eventId}`,
        draftData
      );
      if (showSuccessNotification) {
        showNotification(
          "success",
          "All participant data has been temporarily saved!"
        );
      }
      return true;
    } catch (err) {
      console.error("Error saving to Firestore:", err);
      showNotification("error", "Failed to save participant data to server.");
      return false;
    }
  };

  const handleNextParticipant = async () => {
    const currentParticipant = formData.participants[currentParticipantIndex];
    if (!currentParticipant) {
      console.error(
        "Current participant is undefined at index:",
        currentParticipantIndex
      );
      showNotification(
        "error",
        `Participant data ${currentParticipantIndex + 1} not found.`
      );
      return;
    }
    const { isValid, errors: newErrors } = validateParticipant(
      currentParticipant,
      currentParticipantIndex
    );

    setTouched((prev) => {
      const newTouched = { ...prev };
      Object.keys(currentParticipant).forEach((field) => {
        newTouched[`${field}_${currentParticipantIndex}`] = true;
      });
      return newTouched;
    });

    setErrors((prev) => ({ ...prev, ...newErrors }));

    if (!isValid) {
      const firstErrorKey = Object.keys(newErrors).find(
        (key) => newErrors[key]
      );
      const firstError = firstErrorKey
        ? newErrors[firstErrorKey]
        : "Please complete participant data correctly.";
      showNotification("error", firstError);
      return;
    }

    const success = await saveToFirestore();
    if (!success) return;

    if (currentParticipantIndex === 0 && updateUserData) {
      const updatedFields = {
        namaDepan: currentParticipant.namaDepan,
        namaBelakang: currentParticipant.namaBelakang,
        namaLengkap:
          `${currentParticipant.namaDepan} ${currentParticipant.namaBelakang}`.trim(),
        telepon: currentParticipant.telepon,
        nomorIdentitas: currentParticipant.nomorIdentitas,
        tanggalLahir: currentParticipant.tanggalLahir,
        gender: currentParticipant.gender,
        negara: currentParticipant.negara,
        provinsi: currentParticipant.provinsi,
        kota: currentParticipant.kota,
        alamat: currentParticipant.alamat,
        kontakDarurat: currentParticipant.kontakDarurat,
        namaKontakDarurat: currentParticipant.namaKontakDarurat,
        hubunganKontakDarurat: currentParticipant.hubunganKontakDarurat,
        bibName: currentParticipant.bibName,
        golonganDarah: currentParticipant.golonganDarah,
        sizeJersey: currentParticipant.sizeJersey,
        performanceIndex: currentParticipant.performanceIndex,
        stravaProfile: currentParticipant.stravaProfile,
      };

      try {
        await updateUserData(updatedFields);
        console.log(
          `User data updated for participant 1 in 'users' collection:`,
          updatedFields
        );
      } catch (err) {
        console.error("Error updating user data:", err);
        showNotification("error", "Failed to update user data.");
      }
    }

    if (currentParticipantIndex < 2) {
      setCurrentParticipantIndex(currentParticipantIndex + 1);
      setGenderError("");
    } else {
      const allValid = validateAllParticipants();
      if (allValid) {
        await saveToFirestore(true);
        showNotification(
          "success",
          "All participant data has been temporarily saved! Please proceed to submit."
        );
      } else {
        const firstErrorKey = Object.keys(errors).find((key) => errors[key]);
        const firstError = firstErrorKey
          ? errors[firstErrorKey]
          : genderError ||
            "There are errors in participant data. Please check again.";
        showNotification("error", firstError);
      }
    }
  };

  const handlePreviousParticipant = () => {
    if (currentParticipantIndex > 0) {
      setCurrentParticipantIndex(currentParticipantIndex - 1);
      setGenderError("");
    }
  };

  const openModal = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsModalOpen(true);
  };

  const closeModal = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsModalOpen(false);
  };

  const isNextDisabled = () => {
    if (
      !formData.participants ||
      !formData.participants[currentParticipantIndex]
    ) {
      return true;
    }
    const currentParticipant = formData.participants[currentParticipantIndex];
    const { isValid } = validateParticipant(
      currentParticipant,
      currentParticipantIndex
    );
    return !isValid;
  };

  const getError = (field, participantIndex = null) => {
    const localError =
      participantIndex !== null
        ? errors[`${field}_${participantIndex}`]
        : errors[field];
    const formError =
      participantIndex !== null
        ? formData.errors?.[`${field}_${participantIndex}`]
        : formData.errors?.[field];
    return touched[field] ||
      (participantIndex !== null && touched[`${field}_${participantIndex}`])
      ? localError || formError
      : formError;
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-4">
      {errors.countries && (
        <p className="text-red-500 col-span-full">{errors.countries}</p>
      )}

      {isGroupRegistration &&
      formData.participants &&
      formData.participants.length === 3 ? (
        <>
          <InputField
            label="Team Name"
            name="teamName"
            required
            value={formData.teamName || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled}
            error={getError("teamName")}
            labelColor={labelColor}
          />
          <div className="col-span-full">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">
              Team Participant Data (Required 3 People, At least 1 Female)
            </h3>
            <div className="flex justify-center mb-4">
              {[1, 2, 3].map((step, index) => (
                <div key={index} className="flex items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      index === currentParticipantIndex
                        ? "bg-cyan-600 text-white"
                        : "bg-slate-200 text-slate-800"
                    }`}
                  >
                    {step}
                  </div>
                  {index < 2 && (
                    <div className="w-8 h-1 bg-slate-200 mx-2"></div>
                  )}
                </div>
              ))}
            </div>
            <p className="text-sm text-slate-600 mb-4">
              {currentParticipantIndex === 0
                ? "Fill in your personal data"
                : currentParticipantIndex === 1
                ? "Fill in the first member's data"
                : "Fill in the second member's data"}
            </p>
            {genderError && <p className="text-red-500 mb-4">{genderError}</p>}
            <motion.div
              key={currentParticipantIndex}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.3 }}
              className=""
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputField
                  label="First Name"
                  name="namaDepan"
                  required
                  value={
                    formData.participants[currentParticipantIndex]?.namaDepan ||
                    ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled}
                  error={getError("namaDepan", currentParticipantIndex)}
                  labelColor={labelColor}
                />
                <InputField
                  label="Last Name"
                  name="namaBelakang"
                  required
                  value={
                    formData.participants[currentParticipantIndex]
                      ?.namaBelakang || ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled}
                  error={getError("namaBelakang", currentParticipantIndex)}
                  labelColor={labelColor}
                />
                <InputField
                  label="BIB Name"
                  name="bibName"
                  required
                  value={
                    formData.participants[currentParticipantIndex]?.bibName ||
                    ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled}
                  error={getError("bibName", currentParticipantIndex)}
                  labelColor={labelColor}
                />
                <InputField
                  label="Identity Number (ID Card)"
                  name="nomorIdentitas"
                  required
                  value={
                    formData.participants[currentParticipantIndex]
                      ?.nomorIdentitas || ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled}
                  error={getError("nomorIdentitas", currentParticipantIndex)}
                  labelColor={labelColor}
                />
                <InputField
                  label="Date of Birth"
                  name="tanggalLahir"
                  type="date"
                  required
                  value={
                    formData.participants[currentParticipantIndex]
                      ?.tanggalLahir || ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled}
                  error={getError("tanggalLahir", currentParticipantIndex)}
                  labelColor={labelColor}
                />
                <InputField
                  label="Golongan Darah"
                  name="golonganDarah"
                  type="select"
                  required
                  value={
                    formData.participants[currentParticipantIndex]
                      ?.golonganDarah || ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled}
                  options={[
                    { value: "", label: "Select Blood Type" },
                    { value: "A", label: "A" },
                    { value: "B", label: "B" },
                    { value: "AB", label: "AB" },
                    { value: "O", label: "O" },
                  ]}
                  error={getError("golonganDarah", currentParticipantIndex)}
                  labelColor={labelColor}
                />
                <div className="relative">
                  <InputField
                    label="Jersey Size"
                    name="sizeJersey"
                    type="select"
                    required
                    value={
                      formData.participants[currentParticipantIndex]
                        ?.sizeJersey || ""
                    }
                    onChange={(e) =>
                      handleFieldChange(e, currentParticipantIndex)
                    }
                    disabled={isFormDisabled}
                    options={[
                      { value: "", label: "Select Jersey Size" },
                      { value: "S", label: "S" },
                      { value: "M", label: "M" },
                      { value: "L", label: "L" },
                      { value: "XL", label: "XL" },
                      { value: "XXL", label: "XXL" },
                    ]}
                    error={getError("sizeJersey", currentParticipantIndex)}
                    labelColor={labelColor}
                  />
                  <button
                    type="button"
                    onClick={openModal}
                    className="absolute top-0 right-0 mt-2 mr-2 text-cyan-600 hover:text-cyan-800 text-sm underline"
                    disabled={isFormDisabled}
                  >
                    View Size Chart
                  </button>
                </div>
                <InputField
                  label="Gender"
                  name="gender"
                  type="select"
                  required
                  value={
                    formData.participants[currentParticipantIndex]?.gender || ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled}
                  options={[
                    { value: "", label: "Select Gender" },
                    { value: "Male", label: "Male" },
                    { value: "Female", label: "Female" },
                  ]}
                  error={getError("gender", currentParticipantIndex)}
                  labelColor={labelColor}
                />
                <InputField
                  label="Phone Number/WhatsApp"
                  name="telepon"
                  required
                  value={
                    formData.participants[currentParticipantIndex]?.telepon ||
                    ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled}
                  error={getError("telepon", currentParticipantIndex)}
                  labelColor={labelColor}
                />
                <InputField
                  label="Country"
                  name="negara"
                  type="select"
                  required
                  value={
                    formData.participants[currentParticipantIndex]?.negara || ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled || loading.countries}
                  options={[{ value: "", label: "Select Country" }, ...countries]}
                  error={getError("negara", currentParticipantIndex)}
                  aria-busy={loading.countries}
                  labelColor={labelColor}
                />
                <InputField
                  label="Province"
                  name="provinsi"
                  type="select"
                  required
                  value={
                    formData.participants[currentParticipantIndex]?.provinsi ||
                    ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={
                    isFormDisabled ||
                    !formData.participants[currentParticipantIndex]?.negara ||
                    loading.provinces
                  }
                  options={[
                    { value: "", label: "Select Province" },
                    ...participantProvinces[currentParticipantIndex],
                  ]}
                  error={getError("provinsi", currentParticipantIndex)}
                  aria-busy={loading.provinces}
                  labelColor={labelColor}
                />
                <InputField
                  label="City"
                  name="kota"
                  type="select"
                  required
                  value={
                    formData.participants[currentParticipantIndex]?.kota || ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={
                    isFormDisabled ||
                    !formData.participants[currentParticipantIndex]?.provinsi ||
                    loading.cities
                  }
                  options={[
                    { value: "", label: "Select City" },
                    ...participantCities[currentParticipantIndex],
                  ]}
                  error={getError("kota", currentParticipantIndex)}
                  aria-busy={loading.cities}
                  labelColor={labelColor}
                />
                <InputField
                  label="Address"
                  name="alamat"
                  required
                  value={
                    formData.participants[currentParticipantIndex]?.alamat || ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled}
                  error={getError("alamat", currentParticipantIndex)}
                  labelColor={labelColor}
                />
                <InputField
                  label="Emergency Contact Number"
                  name="kontakDarurat"
                  required
                  value={
                    formData.participants[currentParticipantIndex]
                      ?.kontakDarurat || ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled}
                  error={getError("kontakDarurat", currentParticipantIndex)}
                  labelColor={labelColor}
                />
                <InputField
                  label="Emergency Contact Name"
                  name="namaKontakDarurat"
                  required
                  value={
                    formData.participants[currentParticipantIndex]
                      ?.namaKontakDarurat || ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled}
                  error={getError("namaKontakDarurat", currentParticipantIndex)}
                  labelColor={labelColor}
                />
                <InputField
                  label="Relationship with Emergency Contact"
                  name="hubunganKontakDarurat"
                  required
                  value={
                    formData.participants[currentParticipantIndex]
                      ?.hubunganKontakDarurat || ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled}
                  error={getError(
                    "hubunganKontakDarurat",
                    currentParticipantIndex
                  )}
                  labelColor={labelColor}
                />
                <InputField
                  label="ITRA Performance Index"
                  name="performanceIndex"
                  required
                  value={
                    formData.participants[currentParticipantIndex]
                      ?.performanceIndex || ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled}
                  error={getError("performanceIndex", currentParticipantIndex)}
                  labelColor={labelColor}
                  placeholder="Example: 460"
                />
                <InputField
                  label="STRAVA Profile Link or similar"
                  name="stravaProfile"
                  required
                  value={
                    formData.participants[currentParticipantIndex]
                      ?.stravaProfile || ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled}
                  error={getError("stravaProfile", currentParticipantIndex)}
                  labelColor={labelColor}
                  placeholder="Example: https://www.strava.com/athletes/72528358"
                />
              </div>
            </motion.div>
            <div className="flex justify-between mt-4">
              <button
                type="button"
                onClick={handlePreviousParticipant}
                className={`py-2 px-4 rounded-xl ${
                  currentParticipantIndex === 0
                    ? "bg-slate-300 cursor-not-allowed"
                    : "bg-cyan-600 hover:bg-cyan-700 text-white"
                }`}
                disabled={currentParticipantIndex === 0 || isFormDisabled}
              >
                Previous
              </button>
              <button
                type="button"
                onClick={handleNextParticipant}
                className={`py-2 px-4 rounded-xl ${
                  isNextDisabled()
                    ? "bg-slate-300 cursor-not-allowed"
                    : "bg-cyan-600 hover:bg-cyan-700 text-white"
                }`}
                disabled={isNextDisabled() || isFormDisabled}
              >
                {currentParticipantIndex < 2 ? "Next" : "Finish"}
              </button>
            </div>
          </div>
        </>
      ) : (
        <>
          <InputField
            label="First Name"
            name="namaDepan"
            value={formData.namaDepan || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled}
            required
            error={getError("namaDepan")}
            labelColor={labelColor}
          />
          <InputField
            label="Nama Belakang"
            name="namaBelakang"
            value={formData.namaBelakang || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled}
            required
            error={getError("namaBelakang")}
            labelColor={labelColor}
          />
          {eventId === "JOTR" && (
            <InputField
              label="BIB Name"
              name="bibName"
              value={formData.bibName || ""}
              onChange={(e) => handleFieldChange(e)}
              disabled={isFormDisabled}
              required
              error={getError("bibName")}
              labelColor={labelColor}
            />
          )}
          <InputField
            label="Nomor Identitas (KTP)"
            name="nomorIdentitas"
            value={formData.nomorIdentitas || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled}
            required
            error={getError("nomorIdentitas")}
            labelColor={labelColor}
          />
          <InputField
            label="Tanggal Lahir"
            name="tanggalLahir"
            type="date"
            value={formData.tanggalLahir || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled}
            required
            error={getError("tanggalLahir")}
            labelColor={labelColor}
          />
          {eventId === "JOTR" && (
            <InputField
              label="Golongan Darah"
              name="golonganDarah"
              type="select"
              value={formData.golonganDarah || ""}
              onChange={(e) => handleFieldChange(e)}
              disabled={isFormDisabled}
              required
              options={[
                { value: "", label: "Pilih Golongan Darah" },
                { value: "A", label: "A" },
                { value: "B", label: "B" },
                { value: "AB", label: "AB" },
                { value: "O", label: "O" },
              ]}
              error={getError("golonganDarah")}
              labelColor={labelColor}
            />
          )}
          {eventId === "JOTR" && (
            <div className="relative">
              <InputField
                label="Size Jersey"
                name="sizeJersey"
                type="select"
                value={formData.sizeJersey || ""}
                onChange={(e) => handleFieldChange(e)}
                disabled={isFormDisabled}
                required
                options={[
                  { value: "", label: "Pilih Size Jersey" },
                  { value: "S", label: "S" },
                  { value: "M", label: "M" },
                  { value: "L", label: "L" },
                  { value: "XL", label: "XL" },
                  { value: "XXL", label: "XXL" },
                ]}
                error={getError("sizeJersey")}
                labelColor={labelColor}
              />
              <button
                type="button"
                onClick={openModal}
                className="absolute top-0 right-0 mt-2 mr-2 text-cyan-600 hover:text-cyan-800 text-sm underline"
                disabled={isFormDisabled}
              >
                Lihat Size Chart
              </button>
            </div>
          )}
          <InputField
            label="Gender"
            name="gender"
            type="select"
            value={formData.gender || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled}
            required
            error={getError("gender")}
            labelColor={labelColor}
            options={[
              { value: "", label: "Select Gender" },
              { value: "Male", label: "Male" },
              { value: "Female", label: "Female" },
            ]}
          />
          <InputField
            label="Nomor Telepon/WhatsApp"
            name="telepon"
            value={formData.telepon || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled}
            required
            error={getError("telepon")}
            labelColor={labelColor}
          />
          <InputField
            label="Negara"
            name="negara"
            type="select"
            value={formData.negara || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled || loading.countries}
            options={[{ value: "", label: "Pilih Negara" }, ...countries]}
            required
            error={getError("negara")}
            aria-busy={loading.countries}
            labelColor={labelColor}
          />
          <InputField
            label="Provinsi"
            name="provinsi"
            type="select"
            value={formData.provinsi || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled || !formData.negara || loading.provinces}
            options={[{ value: "", label: "Select Province" }, ...provinces]}
            required
            error={getError("provinsi")}
            aria-busy={loading.provinces}
            labelColor={labelColor}
          />
          <InputField
            label="Kota"
            name="kota"
            type="select"
            value={formData.kota || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled || !formData.provinsi || loading.cities}
            options={[{ value: "", label: "Select City" }, ...cities]}
            required
            error={getError("kota")}
            aria-busy={loading.cities}
            labelColor={labelColor}
          />
          <InputField
            label="Alamat"
            name="alamat"
            value={formData.alamat || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled}
            required
            error={getError("alamat")}
            labelColor={labelColor}
          />
          <InputField
            label="Emergency Contact Number"
            name="kontakDarurat"
            value={formData.kontakDarurat || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled}
            required
            error={getError("kontakDarurat")}
            labelColor={labelColor}
          />
          <InputField
            label="Nama Kontak Darurat"
            name="namaKontakDarurat"
            value={formData.namaKontakDarurat || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled}
            required
            error={getError("namaKontakDarurat")}
            labelColor={labelColor}
          />
          <InputField
            label="Hubungan Dengan Kontak Darurat"
            name="hubunganKontakDarurat"
            value={formData.hubunganKontakDarurat || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled}
            required
            error={getError("hubunganKontakDarurat")}
            labelColor={labelColor}
          />
          {eventId === "JOTR" && (
            <>
              <InputField
                label="ITRA Performance Index"
                name="performanceIndex"
                value={formData.performanceIndex || ""}
                onChange={(e) => handleFieldChange(e)}
                disabled={isFormDisabled}
                required
                error={getError("performanceIndex")}
                labelColor={labelColor}
                placeholder="Contoh: 460"
              />
              <InputField
                label="Link Profile STRAVA atau sejenis"
                name="stravaProfile"
                value={formData.stravaProfile || ""}
                onChange={(e) => handleFieldChange(e)}
                disabled={isFormDisabled}
                required
                error={getError("stravaProfile")}
                labelColor={labelColor}
                placeholder="Contoh: https://www.strava.com/athletes/72528358"
              />
            </>
          )}
        </>
      )}

      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 flex items-center justify-center z-50"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.8 }}
              className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[80vh] overflow-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={sizeChartUrl}
                alt="Size Chart"
                className="w-full h-auto"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
